"use client";
import Banner from "@marginly/ui/components/banner";
import T from "@marginly/ui/components/typography";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAccount } from "wagmi";

import Swipeable from "@/components/common/swipeable";
import { INVALID_AMOUNT_ERROR } from "@/components/trade/constants";
import Details from "@/components/trade/partials/details";
import InputPartial from "@/components/trade/partials/input";
import PendingTxPartial, {
  PendingTxType,
} from "@/components/trade/partials/pending-tx";
import WithdrawAllPartial from "@/components/trade/partials/withdraw-all";
import {
  Form,
  OutOfForm,
  TransactionFee,
} from "@/components/trade/trade.styled";
import { Noop } from "@/components/util";
import { SUPPORTED_CHAINS } from "@/constants/chains";
import { ZERO } from "@/constants/math";
import { useRx } from "@/contexts/rx";
import { useWeb3Context } from "@/contexts/web3";
import { useApprove } from "@/hooks/approve";
import { withExactMessage } from "@/util/error";
import { formatCoin } from "@/util/format-coin";
import {
  trackTradeApproveClick,
  trackTradeClick,
  trackTradeSignInClick,
  trackTradeWithdrawClick,
} from "@/util/tracking";

import Portal from "../common/portal";
import Space from "../common/space";
import SwitchNetworkOr from "../common/switch-network-or";
import RangeSlider from "../ui/range-slider";
import TradeContext from "./context";
import { LeverageAlertModal } from "./leverage-alert-modal";
import { TradeTabs } from "./partials/tabs/tabs";
import { PendingBlock } from "./styled";
import { TradeActionButton } from "./trade-action-button";
import useTrade, { useSetInitialAmount } from "./use-trade";

const getSafeLeverage = (maxLeverage: number) => maxLeverage - 1;

export default function TradeForm({
  chainName,
  flip,
}: {
  chainName: string;
  flip?: boolean;
}) {
  const { connect$, error$ } = useRx();
  const chain = SUPPORTED_CHAINS[chainName as keyof typeof SUPPORTED_CHAINS];
  const native = chain.nativeCurrency;

  const {
    address,
    base,
    depositAmount,
    direction,
    quote,
    flags,
    token,
    maxLeverage,
    realBaseAmount,
    realQuoteAmount,
    unwrap,
    baseBalance,
    quoteBalance,
    basePrice,
    dispatch,
  } = useContext(TradeContext);

  const selectedToken = token ? { base, quote }[token] : undefined;
  const [isLeverageModalOpen, setLeverageModalStatus] = useState(false);
  const { realAmount } = useSetInitialAmount();

  // todo how to pass refetch more gracefully?
  const refetchRef = useRef<() => Promise<void>>();

  const { getPoolByAddress } = useWeb3Context();
  const isFarm = getPoolByAddress(address)?.pool.isFarm;
  const currentPosAmount = !flags?.flip
    ? direction === "long"
      ? realBaseAmount
      : realQuoteAmount
    : realAmount;

  // fixme
  const approveAmount = ZERO.plus(depositAmount ?? 0)
    .minus(currentPosAmount ?? 0)
    .toString(10);

  const approveToken = selectedToken?.address;
  const unwrapToken = { base, quote }[unwrap!];

  const approve = useApprove({
    token: unwrapToken?.address === approveToken ? undefined : approveToken,
    spender: address,
    amount: approveAmount,
    refetch: refetchRef.current,
  });

  const {
    buttonState,
    cancelRef,
    isWithdrawPanelVisible,
    fundingRate,
    handleSubmit,
    hideWithdrawPanel,
    leverage,
    liquidationPrice,
    minPositionAmount,
    notEnoughFunds,
    onInputChange,
    pending,
    pendingTitle,
    positionDesc,
    positionSize,
    swapFee,
    withdrawPanelClass,
    withdrawAmount,
    withdrawSymbol,
    minPositionSize,
  } = useTrade(
    {
      needApprove: approve.needApprove,
      realAmount,
      refetchRef,
      unwrap: unwrapToken?.address,
    },
    chainName,
  );

  const account = useAccount();
  const connectAccount = useCallback(() => connect$.next(true), [connect$]);
  const maxSliderValue = maxLeverage ?? 20;
  const depositAmountNum = Number(depositAmount);
  const safeLeverage = getSafeLeverage(maxSliderValue);
  const defaultLeverage = Math.ceil(0.4 * maxSliderValue);
  const isCriticalLeverage = (leverage ?? 0) > safeLeverage;

  const shouldWithdraw = ["shouldWithdrawBase", "shouldWithdrawQuote"].includes(
    buttonState,
  );

  const submit = useCallback(() => {
    // fixme becoming too complicated

    if (typeof window !== "undefined") {
      // use instead `ref.current?.blur()` relying on useClickOutside behavior
      window.dispatchEvent(new Event("click"));
    }

    if (["shouldWithdrawBase", "shouldWithdrawQuote"].includes(buttonState)) {
      trackTradeWithdrawClick();
      return handleSubmit();
    }

    if (!depositAmountNum || !Number.isFinite(depositAmountNum)) {
      return error$.next(withExactMessage(new Error(INVALID_AMOUNT_ERROR)));
    }

    if (!account.isConnected) {
      trackTradeSignInClick();
      return connectAccount();
    }

    /** @deprecated */
    const notEnough = notEnoughFunds;

    if (approve.needApprove && !notEnough) {
      trackTradeApproveClick(direction);
      return approve.submit();
    }

    direction && trackTradeClick(direction);
    return handleSubmit();
  }, [
    depositAmountNum,
    buttonState,
    account.isConnected,
    direction,
    notEnoughFunds,
    approve,
    handleSubmit,
    error$,
    connectAccount,
  ]);

  approve.setCallback(submit);

  const executeWithLeverageModal = (
    callback: () => void,
    isLeverageModal?: true,
  ) => {
    if (
      isCriticalLeverage &&
      !isLeverageModal &&
      account.isConnected &&
      !shouldWithdraw
    ) {
      onInputChange?.({ leverage: safeLeverage });
      return setLeverageModalStatus(true);
    }
    setLeverageModalStatus(false);
    callback();
  };

  const bodyOverflow =
    approve.visible || pending || isWithdrawPanelVisible ? "hidden" : "visible";

  useEffect(() => {
    if (typeof window !== "undefined") {
      document.body.style.overflow = bodyOverflow;
    }
  }, [bodyOverflow]);

  const { chain: walletChain } = account;

  const renderTradeActionButton = (isLeverageModal?: true) => (
    <div className="action-button">
      <TradeActionButton
        basePrice={basePrice}
        shouldWithdraw={shouldWithdraw}
        needApprove={approve.needApprove}
        baseSymbol={base?.symbol}
        buttonState={buttonState}
        direction={direction}
        minPositionAmount={minPositionAmount}
        minPositionSize={minPositionSize}
        pending={pending}
        positionDesc={positionDesc}
        positionSizeTotal={positionSize?.total}
        quoteSymbol={quote?.symbol}
        withdrawSymbol={withdrawSymbol}
        onClick={() => {
          executeWithLeverageModal(submit, isLeverageModal);
        }}
        chainName={chainName}
        token={token}
      />
    </div>
  );

  const isLoaded = Boolean(quote?.symbol && maxLeverage);

  return (
    <>
      <Swipeable>
        {approve.visible &&
        approve.pendingTxType &&
        walletChain &&
        walletChain.id === chain?.id ? (
          <Portal>
            <PendingTxPartial
              cancel={approve.cancel}
              type={approve.pendingTxType}
              title={approve.title}
              submit={approve.submit}
            ></PendingTxPartial>
          </Portal>
        ) : pendingTitle ? (
          <Portal>
            <PendingTxPartial
              cancel={cancelRef.current}
              title={pendingTitle}
              type={PendingTxType.Pending}
            />
          </Portal>
        ) : (
          <Noop />
        )}
        {isWithdrawPanelVisible && (
          <WithdrawAllPartial
            amount={withdrawAmount}
            containerClassName={withdrawPanelClass}
            token={withdrawSymbol}
            close={hideWithdrawPanel}
            submit={handleSubmit}
          />
        )}

        {isLoaded ? (
          <Form>
            <TradeTabs
              direction={direction}
              onChange={onInputChange}
              disableShort={isFarm}
            />
            <InputPartial
              minPositionSize={minPositionSize}
              defaultLeverage={defaultLeverage}
              amount={depositAmount}
              isCritical={isCriticalLeverage}
              direction={direction}
              leverage={leverage}
              error={notEnoughFunds}
              onChange={onInputChange}
              onTokenChange={(token) => {
                basePrice
                  ? dispatch?.({
                      type: "setToken",
                      token,
                      basePrice,
                      decimals:
                        (token === "base" ? base?.decimals : quote?.decimals) ??
                        // fixme
                        0,
                    })
                  : undefined;
              }}
              maxLeverage={maxSliderValue}
              token={token}
              unwrap={unwrap}
              position={realAmount}
              base={base}
              quote={quote}
              baseBalance={baseBalance}
              quoteBalance={quoteBalance}
              flip={flip}
            >
              {realAmount?.gt(0) ? (
                <>
                  <Space height={12} heightMobile={12} />
                  <Banner accent md>
                    <img src="/images/info-dark.svg" alt="" />
                    <T body>
                      You already lend. Enter at least {formatCoin(realAmount)}{" "}
                      {selectedToken?.address === unwrapToken?.address
                        ? native.symbol
                        : selectedToken?.symbol}{" "}
                      to&nbsp;go&nbsp;{direction}.
                    </T>
                  </Banner>
                </>
              ) : (
                <Noop />
              )}
            </InputPartial>
            <Space height={48} heightMobile={48} />

            <SwitchNetworkOr chainName={chainName}>
              {renderTradeActionButton()}
            </SwitchNetworkOr>

            {buttonState === "notEnoughFunds" ? (
              <div></div>
            ) : (
              <div>
                <Space height={16} heightMobile={16} />
                <TransactionFee>
                  {approve.needApprove
                    ? "Approval is required"
                    : swapFee &&
                      quote && (
                        <>
                          {formatCoin(swapFee)} {quote.symbol ?? ""} service fee
                        </>
                      )}
                </TransactionFee>
              </div>
            )}

            <OutOfForm>
              {!!depositAmount &&
                !!liquidationPrice &&
                !!fundingRate &&
                !!quote &&
                account.isConnected && (
                  <>
                    <Space height={0} heightMobile={24} />
                    <Details
                      fundingRate={fundingRate}
                      liquidationPrice={liquidationPrice}
                      quoteSymbol={quote.symbol}
                      className="details"
                    />
                  </>
                )}
            </OutOfForm>
            {isLeverageModalOpen && (
              <LeverageAlertModal
                leverage={safeLeverage}
                onCancel={() => setLeverageModalStatus(false)}
              >
                <RangeSlider
                  title={"Leverage"}
                  max={maxSliderValue}
                  value={leverage ?? defaultLeverage}
                  handleChange={(e) =>
                    onInputChange?.({ leverage: Number(e.target.value) })
                  }
                  isCritical={isCriticalLeverage}
                />
                <Space height={48} heightMobile={48} />
                {renderTradeActionButton(true)}
              </LeverageAlertModal>
            )}
          </Form>
        ) : (
          <PendingBlock />
        )}
      </Swipeable>
    </>
  );
}
